import type { MouseEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { POPPER_HEADER_HEIGHT, setAlpha } from '../../styles';
import { IconButton } from '../Button';
import type { BoxProps } from '../Core';
import { Flex, HStack } from '../Core';
import { FormControlSizes } from '../Form/types';
import { Icon, IconName } from '../Icons';
import { Text } from '../Text';

export interface PopperHeaderProps {
  icon?: IconName;
  iconColor?: string;
  title?: ReactNode;
  /** Rendered before the title, with a chevron at the end */
  titlePrefix?: ReactNode;
  /** Prefix?, rendered after the title prefix */
  prefix?: ReactNode;
  /** Suffix?, rendered before the close button */
  suffix?: ReactNode;
  /** If true, will disable the close button (X) in the header */
  closeDisabled?: boolean;
  onCloseClicked?: (e: MouseEvent<HTMLButtonElement>) => void;

  boxProps?: BoxProps;
  suffixFlexProps?: BoxProps;
}

/** This component exists to be used as a header component for all "popper" components -- modals, dropdowns, popovers, etc */
export function PopperHeader({
  icon,
  iconColor,
  title,
  titlePrefix,
  prefix,
  suffix,
  onCloseClicked,
  closeDisabled,
  suffixFlexProps,
  boxProps,
  ...props
}: PopperHeaderProps) {
  return (
    <HeaderHStack pl="spacing8" pr="spacing4" {...boxProps} {...props} data-testid="popper-header">
      <Flex alignItems="center" gap="spacing4" color="colorTextImportant" flex="1 1 auto">
        {icon && <Icon icon={icon} color={iconColor ?? 'colorTextDefault'} />}
        {titlePrefix && (
          <TitlePrefix data-testid="popper-header-title-prefix">
            {titlePrefix}
            <Icon icon={IconName.ChevronRight} />
          </TitlePrefix>
        )}
        {title}
        {prefix}
      </Flex>

      <HStack gap="spacing4" {...suffixFlexProps}>
        {suffix}
        {onCloseClicked && (
          <IconButton
            ghost
            color="colorTextDefault"
            size={FormControlSizes.Small}
            icon={IconName.Close}
            onClick={onCloseClicked}
            disabled={closeDisabled}
            data-testid="close-dialog-button"
          />
        )}
      </HStack>
    </HeaderHStack>
  );
}

const HeaderHStack = styled(HStack)`
  gap: ${({ theme }) => theme.spacing12}px;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  background: ${({ theme }) => theme.backgroundModalHeader};
  min-height: ${POPPER_HEADER_HEIGHT}px;
  border-bottom: ${({ theme }) => `1px solid ${theme.backgroundDivider}`};
  border-top-right-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  border-top-left-radius: ${({ theme }) => theme.borderRadiusDefault}px;
`;

const TitlePrefix = styled(HStack)`
  color: ${({ theme }) => setAlpha(0.56, theme.colors.gray['100'])};
  gap: ${({ theme }) => theme.spacing4}px;
`;

interface PathTitleProps {
  paths: string[];
}

/**
 * A component for rendering a path title of sorts. Taking in an array of paths,
 * will render something like Path1 > Path2 > Path3
 * The last part of the Path will be highlighted (color text-wise)
 */
export const PathTitle = ({ paths }: PathTitleProps) => {
  return (
    <HStack gap="spacing4">
      {paths.map((path, i) => {
        const isLast = i === paths.length - 1;
        return (
          <>
            <Text color={isLast ? 'colorTextImportant' : 'colorTextDefault'}>{path}</Text>
            {!isLast && <Icon icon={IconName.ChevronRight} size="fontSizeSmall" />}
          </>
        );
      })}
    </HStack>
  );
};
